import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Leaf, Droplets, Wrench } from 'lucide-react';
import './Services.css';

const Services = () => {
  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <h1 className="display-medium">Our Services</h1>
          <p className="body-large" style={{ marginTop: '16px', maxWidth: '700px' }}>
            Professional lawn renovation services for Perth's Northern Suburbs. We remove thatch, relieve compaction, and restore your lawn to peak health.
          </p>
          <div className="hero-buttons">
            <a href="tel:0417910217" className="btn-primary">
              <Phone size={20} />
              Call 0417 910 217
            </a>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="main-services">
        <div className="container">
          <div className="service-detail-card">
            <div className="service-icon-wrapper">
              <Leaf size={48} color="#004534" />
            </div>
            <div className="service-content">
              <h2 className="heading-1">Verti-Mowing & Dethatching</h2>
              <p className="body-large" style={{ marginTop: '16px' }}>
                Verti-mowing (vertical mowing) is the most effective way to remove built-up thatch — the spongy layer of dead grass and organic matter that sits between your lawn's green growth and the soil.
              </p>
              
              <h3 className="heading-3" style={{ marginTop: 'var(--spacing-large)' }}>What We Do</h3>
              <ul className="service-list">
                <li><CheckCircle size={20} color="#004534" /> Cut vertically through the thatch layer using specialized equipment</li>
                <li><CheckCircle size={20} color="#004534" /> Remove nearly 10 years worth of dead buildup in a single session</li>
                <li><CheckCircle size={20} color="#004534" /> Eliminate the "spongy" feel and restore firmness</li>
                <li><CheckCircle size={20} color="#004534" /> Improve water and nutrient penetration to the root zone</li>
                <li><CheckCircle size={20} color="#004534" /> Encourage new, healthy leaf growth from the crown</li>
              </ul>

              <h3 className="heading-3" style={{ marginTop: 'var(--spacing-large)' }}>What to Expect</h3>
              <p className="body-medium">
                Your lawn will look "cut right back" immediately after verti-mowing — sometimes down to bare soil in heavily thatched areas. This is normal and necessary. Within 2-4 weeks of proper watering and care, you'll see vigorous new growth coming through. Most customers report their lawn is the "best in the street" within 6-8 weeks.
              </p>
            </div>
          </div>

          <div className="service-detail-card">
            <div className="service-icon-wrapper">
              <Droplets size={48} color="#004534" />
            </div>
            <div className="service-content">
              <h2 className="heading-1">Lawn Coring & Aeration</h2>
              <p className="body-large" style={{ marginTop: '16px' }}>
                Lawn coring (also called aeration) removes small plugs of soil from your lawn to relieve compaction and improve the health of your root system.
              </p>
              
              <h3 className="heading-3" style={{ marginTop: 'var(--spacing-large)' }}>What We Do</h3>
              <ul className="service-list">
                <li><CheckCircle size={20} color="#004534" /> Remove small soil cores across the entire lawn</li>
                <li><CheckCircle size={20} color="#004534" /> Break up compacted soil caused by foot traffic and mowing</li>
                <li><CheckCircle size={20} color="#004534" /> Improve air, water, and nutrient movement to roots</li>
                <li><CheckCircle size={20} color="#004534" /> Enhance drainage and reduce waterlogging</li>
                <li><CheckCircle size={20} color="#004534" /> Promote deeper, stronger root growth</li>
              </ul>

              <h3 className="heading-3" style={{ marginTop: 'var(--spacing-large)' }}>When to Core</h3>
              <p className="body-medium">
                Coring is especially beneficial for lawns with heavy clay soil, high foot traffic, or poor drainage. We often recommend combining verti-mowing and coring for the best results, particularly for lawns that haven't been renovated in several years.
              </p>
            </div>
          </div>

          <div className="service-detail-card">
            <div className="service-icon-wrapper">
              <Wrench size={48} color="#004534" />
            </div>
            <div className="service-content">
              <h2 className="heading-1">Garden Clean-Ups</h2>
              <p className="body-large" style={{ marginTop: '16px' }}>
                Beyond lawn renovation, we also provide comprehensive garden clean-up services to restore overgrown or neglected gardens.
              </p>
              
              <h3 className="heading-3" style={{ marginTop: 'var(--spacing-large)' }}>What We Do</h3>
              <ul className="service-list">
                <li><CheckCircle size={20} color="#004534" /> Remove overgrown weeds and unwanted vegetation</li>
                <li><CheckCircle size={20} color="#004534" /> Trim and shape hedges and shrubs</li>
                <li><CheckCircle size={20} color="#004534" /> Clear garden beds and mulch areas</li>
                <li><CheckCircle size={20} color="#004534" /> Remove green waste and debris</li>
                <li><CheckCircle size={20} color="#004534" /> Restore garden beds to a neat, manageable state</li>
              </ul>

              <p className="body-medium" style={{ marginTop: 'var(--spacing-medium)' }}>
                We go the extra mile to make your garden look great again — whether it's a massive clean-up or regular maintenance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Aftercare Section */}
      <section className="aftercare-section">
        <div className="container">
          <div className="aftercare-card">
            <h2 className="heading-1">Aftercare Guidance</h2>
            <p className="body-large" style={{ marginTop: '16px' }}>
              Proper aftercare is essential to maximize your results. We provide personalized advice based on your lawn type, season, and condition.
            </p>
            <div className="aftercare-grid">
              <div className="aftercare-item">
                <h3 className="heading-3">Watering</h3>
                <p className="body-medium">
                  Keep the lawn moist (not soaked) for the first 2-3 weeks to encourage new growth. Water lightly every 1-2 days, adjusting for weather.
                </p>
              </div>
              <div className="aftercare-item">
                <h3 className="heading-3">Mowing</h3>
                <p className="body-medium">
                  Wait until new growth reaches at least 5-7cm before mowing. Set your mower to a higher setting initially to avoid stressing the new shoots.
                </p>
              </div>
              <div className="aftercare-item">
                <h3 className="heading-3">Fertilising</h3>
                <p className="body-medium">
                  A light application of slow-release fertiliser 2-3 weeks after renovation can boost growth. Ask us for specific product recommendations.
                </p>
              </div>
              <div className="aftercare-item">
                <h3 className="heading-3">Topdressing</h3>
                <p className="body-medium">
                  For severely thinned areas, a light topdressing of quality lawn mix can help level the surface and encourage coverage. We can advise on this.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta">
        <div className="container">
          <div className="cta-banner">
            <h2 className="heading-1" style={{ color: 'white' }}>Ready to Transform Your Lawn?</h2>
            <p className="body-large" style={{ color: 'rgba(255, 255, 255, 0.9)', marginTop: '16px' }}>
              Get expert advice and a free quote today.
            </p>
            <div className="cta-buttons">
              <a href="tel:0417910217" className="btn-cta" style={{ background: 'white', color: 'var(--brand-dark)' }}>
                <Phone size={20} />
                Call 0417 910 217
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Sticky CTA */}
      <div className="mobile-cta-sticky">
        <a href="tel:0417910217" className="btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
          <Phone size={18} />
          Call Now: 0417 910 217
        </a>
      </div>
    </div>
  );
};

export default Services;